import type { FC, PropsWithChildren } from 'react'

import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

type ColData = {
  heading: string
  description: string
}

const copy = {
  heading: `Product Security`,
}

const columnTrackData: ColData[] = [
  {
    heading: `Manage access to your account`,
    description: `Centrally manage your policies for access with Single Sign-On (SSO) on the Business plan.`,
  },
  {
    heading: `Control visibility with user access levels`,
    description: `Control access to your Sources and Workspaces with fine-grained permissions to manage how your users interact with your data.`,
  },
  {
    heading: `System for Cross-domain Identity Management (SCIM)`,
    description: `SCIM allows your Identity Provider (IdP) to manage users and group membership within the Segment application.`,
  },
  {
    heading: `Password guidance`,
    description: `When choosing a new password, we provide visual guidance to help customers pick strong passwords that have not been exposed in security breaches on other websites.`,
  },
  {
    heading: `Multi-factor authentication (MFA)`,
    description: `MFA provides an additional layer of security beyond your username and password. When logging into Segment, you’ll also enter a code from your mobile phone.`,
  },
]

const ProductSecurity: FC<PropsWithChildren<unknown>> = () => {
  return (
    <StyleSectionBase variant="centered">
      <SectionCopy>
        <CopyTextBase>
          <StyledCopyTextHeading variant="h2">{copy.heading}</StyledCopyTextHeading>
        </CopyTextBase>
      </SectionCopy>
      <Section.Contents>
        <StyledColumnTrack columns={1} overrides={{ lg: 3 }} center={false}>
          {columnTrackData.map(({ heading, description }) => (
            <FeatureBoxBase alignment="left" key={heading}>
              <FeatureBox.Body>
                <FeatureBox.Title>{heading}</FeatureBox.Title>
                <FeatureBox.Text>{description}</FeatureBox.Text>
              </FeatureBox.Body>
            </FeatureBoxBase>
          ))}
        </StyledColumnTrack>
      </Section.Contents>
    </StyleSectionBase>
  )
}

const StyledCopyTextHeading = styled(CopyText.Heading)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    text-align: center;
  }
`

const StyledColumnTrack = styled(ColumnTrack)`
  align-items: flex-start;
  row-gap: 40px;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    row-gap: 60px;
  }
`

const StyleSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    justify-items: flex-start;
    row-gap: 60px;

    ${({ theme }) => theme.breakpoints.up(`md`)} {
      max-width: 720px;
      padding: 120px 0;
      margin: auto;
    }

    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      max-width: max-content;
      padding: 80px 80px 120px;
      justify-items: center;
    }
  }
`

const SectionCopy = styled(Section.Copy)`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    width: 100%;
  }
  ${Section.Wrapper} {
    margin: 0 auto;
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      margin: 0;
    }
  }
`

export default ProductSecurity
